import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import NewsCard from "./NewsCard";
import Button from "./ui/Button";
import { useState, useMemo } from "react";
import { ButtonVariant } from "../types/constants";
import { newsServiceFactory } from "../services/NewsServiceImpl";
import { useQuery } from "@tanstack/react-query";
import { useBreakpoint } from "../custom-hooks/useBreakpoint";

const newsService = newsServiceFactory();

export default function News() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isMobile, isTablet } = useBreakpoint();
    const [activeIndex, setActiveIndex] = useState(0);

    const { data: news = [] } = useQuery({
        queryKey: ["news", 1, 3, i18n.language],
        queryFn: async () => {
            const { rows } = await newsService.getAllNews(1, 3);
            return rows;
        },
    });

    const heading = t("home.headings.news");
    const buttonText = t("buttons.moreNews");

    const tabletSlides = useMemo(() => {
        if (news.length < 2) return [];
        const slides: number[][] = [];
        for (let i = 0; i < news.length; i++) {
            const nextIndex = (i + 1) % news.length;
            slides.push([i, nextIndex]);
        }
        return slides;
    }, [news]);

    let cardsToShow;
    if (isMobile) {
        cardsToShow = [news[activeIndex]];
    } else if (isTablet) {
        cardsToShow = tabletSlides[activeIndex]?.map((i) => news[i]).filter(Boolean) || [];
    } else {
        cardsToShow = news;
    }

    const dotsCount = isMobile ? news.length : isTablet ? tabletSlides.length : 0;
    if (!news.length) return null;
    return (
        <section className="w-full bg-background-100">
            <div className="max-w-[1280px] mx-auto">
                <h2 className="font-geologica font-medium text-[32px] uppercase text-primary-400 mb-[50px]">
                    {heading}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lap:grid-cols-3 gap-5 md:gap-4">
                    {cardsToShow.map((card) => (
                        <NewsCard key={card.id} news={card} showDate={false} />
                    ))}
                </div>

                {(isMobile || isTablet) && (
                    <div
                        className={`flex justify-center items-center gap-3 ${isTablet ? "mt-8" : "mt-4"} ${
                            isMobile ? "mb-8" : ""
                        }`}
                    >
                        {Array.from({ length: dotsCount }).map((_, i) => (
                            <div
                                key={i}
                                onClick={() => setActiveIndex(i)}
                                className={`w-3 h-3 rounded-full cursor-pointer transition-colors duration-300 ${i === activeIndex ? "bg-primary-400" : "bg-primary-600"
                                    }`}
                            />
                        ))}
                    </div>
                )}

                <div className={`flex justify-start ${isMobile ? "mt-0" : "mt-[50px]"}`}>
                    <Button text={buttonText} variant={ButtonVariant.PRIMARY} onClick={() => navigate("/news")} />
                </div>
            </div>
        </section>
    );
}
